import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Checkbox, HStack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { GptRequestStatuses } from '../../../../../../../../constants/gptRequestStatuses';
import { hideModal, showModal } from '../../../../../../../../redux/modules/modal';
import {
  useActiveRecordDocuments,
  useAiScanDocs
} from '../../../../../../../../services/record-service';
import FileNameCell from '../DocumentRequirements/components/DocumentsTableContainer/components/DocumentsTable/cells/FileNameCell';

import ScanProgressBar from './ScanProgressBar';

const failureMessage = gptStatus => {
  switch (gptStatus) {
    case GptRequestStatuses.DocumentSizeTooLarge:
      return 'The most recent document scan failed because one or more documents exceed the scan limits. '
        + 'Would you like to try again with different documents?';
    case GptRequestStatuses.NoQuestionsContent:
      return 'The most recent document scan was skipped because this record has no questions.';
    case GptRequestStatuses.NoDocumentsContent:
      return 'The most recent document scan was skipped because no documents were selected. '
        + 'Would you like to try again?';
    default:
      return 'The most recent document scan failed.  Would you like to try again?';
  }
};

const FailedScanRetry = ({ recordId, gptStatus }) => {
  const dispatch = useDispatch();
  const { data: activeRecordDocuments, isLoading } = useActiveRecordDocuments({ recordId });
  const { mutateAsync: initializeAiScan } = useAiScanDocs({ recordId });

  const uniqueDocs = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return [...new Map(activeRecordDocuments.map(doc => [doc.fileUniqueId, doc])).values()];
  }, [activeRecordDocuments]);

  const [retryScan, setRetryScan] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState({});

  const handleDocCheck = (docId, checked) => {
    setSelectedDocs(prev => ({
      ...prev,
      [docId]: checked
    }));
  };

  const handleSubmit = async () => {
    const selectedDocIds = Object.keys(selectedDocs)
      .filter(docId => selectedDocs[docId])
      .map(docId => parseInt(docId, 10));

    const resp = await initializeAiScan({ docIds: selectedDocIds });

    dispatch(hideModal());
    dispatch(showModal(
      () => <ScanProgressBar gptRequestId={resp.gptRequestId} recordId={recordId} />,
      { size: '2xl' }
    ));
  };

  return (
    <Box my={4}>
      {!retryScan && (
        <>
          <Text mb={4}>
            {failureMessage(gptStatus)}
          </Text>

          {gptStatus !== GptRequestStatuses.NoQuestionsContent && (
            <Button
              mb={8}
              variant="primary"
              onClick={() => setRetryScan(!retryScan)}
            >
              Retry Scan
            </Button>
          )}
        </>
      )}

      {retryScan && (
        <Box my={4} textAlign="left">
          <Text mb={4}>Select which documents you would like to use for this scan:</Text>

          <Box my="auto">
            {uniqueDocs?.map(document => (
              <HStack key={document.id} mb={4} spacing={4}>
                <Checkbox
                  onChange={e => handleDocCheck(document.id, e.target.checked)}
                />
                <FileNameCell
                  hideOptions
                  document={document}
                  key={document.id}
                  recordId={recordId}
                />
              </HStack>
            ))}
          </Box>

          <HStack justifyContent="center" mt={4}>
            <Button
              variant="secondary"
              onClick={() => setRetryScan(!retryScan)}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

FailedScanRetry.propTypes = {
  recordId: PropTypes.number.isRequired,
  gptStatus: PropTypes.string.isRequired
};

export default FailedScanRetry;
